<template>
  <div class="cancel-subscription">
    <el-row>
      <organization-breadcrumb
        :items="[
          $t('plan_payment.title'),
          $t('plan_payment.cancel_subscription.title'),
        ]"
      />
    </el-row>
    <div class="payment" v-if="payment">
      <base-text :title="true">
        {{ $t("plan_payment.cancel_subscription.title") }}
      </base-text>
      <base-text>
        {{
          $t("plan_payment.cancel_subscription.desc", {
            date: formatDate(payment.plan_detail.next_bill_date),
          })
        }}
      </base-text>
      <div class="plan-details">
        <el-row>
          <el-col>
            <h2>{{ payment.plan }}</h2>
          </el-col>
        </el-row>
        <el-row class="mt-px-16">
          <el-col>
            <p>
              {{
                $t("organizations.plan.user_month", [
                  formatCurrency(payment.price_per_month, payment.currency),
                ])
              }}
            </p>
          </el-col>
        </el-row>
        <hr />
        <el-row class="mt-px-16">
          <el-col class="label" :span="6">
            {{ $t("organizations.plan.purchase_date") }}
          </el-col>
          <el-col class="value" :span="6">
            {{ formatDate(payment.created_at) }}
          </el-col>
        </el-row>
        <el-row class="mt-px-16">
          <el-col class="label" :span="6">
            {{ $t("organizations.plan.billing_cycle") }}
          </el-col>
          <el-col class="value" :span="6">
            {{ payment.cycle }}
          </el-col>
        </el-row>
        <el-row class="mt-px-16">
          <el-col class="label" :span="6">
            {{ $t("organizations.plan.next_billing_date") }}
          </el-col>
          <el-col class="value" :span="6">
            {{ formatDate(payment.plan_detail.next_bill_date) }}
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" class="mt-px-48">
          <el-col :span="8">
            <primary-button type="button" class="bigger" @click.native="goBack">
              {{ $t("plan_payment.cancel_subscription.keep_plan") }}
            </primary-button>
          </el-col>
          <el-col :span="12">
            <el-link
              type="danger"
              :underline="false"
              @click="confirmDialogVisible = true"
            >
              {{ $t("plan_payment.cancel_subscription.cancel_anyway") }}
            </el-link>
          </el-col>
        </el-row>
      </div>
    </div>
    <el-dialog :visible.sync="confirmDialogVisible" width="20%" center>
      <span class="dialog-title">
        {{ $t("plan_payment.cancel_subscription.confirm_title") }}
      </span>
      <span slot="footer" class="dialog-footer">
        <el-button :loading="loading" @click="deleteSubscription" round>
          {{ $t("plan_payment.cancel_subscription.confirm_yes") }}
        </el-button>
        <el-link @click="confirmDialogVisible = false" :underline="false">
          {{ $t("plan_payment.cancel_subscription.confirm_no") }}
        </el-link>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import BaseText from "@/components/common/BaseText.vue";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import axios from "@/utils/axios";
import { PLAN_MONTH_DURATION } from "@/constants/generic";
import { format } from "date-fns";
import { currencyLocaleMapping } from "@/helpers/currencies";
import { utcToZonedTime } from "date-fns-tz";
import { successMessage, warningMessage } from "@/utils/messages";

export default {
  components: {
    BaseText,
    PrimaryButton,
    OrganizationBreadcrumb,
  },
  data() {
    return {
      payment: undefined,
      confirmDialogVisible: false,
      loading: false,
      subscriptionId: undefined,
      planDict: {
        one_year_credit: this.$t("organizations.plan.elsa_pro_1_year"),
        six_months_credit: this.$t("organizations.plan.elsa_pro_6_months"),
        three_months_credit: this.$t("organizations.plan.elsa_pro_3_months"),
        one_month_credit: this.$t("organizations.plan.elsa_pro_1_month"),
      },
      planNameSub: {
        one_year_credit: this.$t("organizations.plan.annual"),
        six_months_credit: this.$t("organizations.plan.semi_annual"),
        three_months_credit: this.$t("organizations.plan.quarterly"),
      },
      planTypeNameSub: {
        medium: this.$t("organizations.plan.medium"),
        lite: this.$t("organizations.plan.lite"),
      },
      planCycleDict: {
        one_year_credit: this.$t("organizations.plan.yearly"),
        six_months_credit: this.$t("organizations.plan.every_6_months"),
        three_months_credit: this.$t("organizations.plan.every_3_months"),
        one_month_credit: this.$t("organizations.plan.every_1_month"),
      },
      dateFormat: "MMMM dd, yyyy",
    };
  },
  computed: {
    organizationId() {
      return this.$store.state.organization.currentOrganizationId;
    },
    organizationData() {
      return this.$store.state.organization.currentOrganization;
    },
    headers() {
      return {
        store: "stripe",
        "x-session-token": localStorage.getItem("payment-token"),
        "x-org-token": Buffer.from(
          `${this.organizationId} ${Date.now()}`
        ).toString("base64"),
      };
    },
  },
  async mounted() {
    const organizationId = this.organizationId;
    const paymentId = Number(this.$route.params.paymentId);
    if (!this.organizationData) {
      await this.$store.dispatch("organization/getCurrentOrganization", {
        organizationId,
      });
    }
    await this.getPayment(paymentId);
  },
  methods: {
    async getPayment(paymentId) {
      let url = `v1/checkouts/${this.organizationId}/payments`;
      const response = await axios.get(url);
      this.payment = response.data.find((item) => item.id === paymentId);
      this.payment.plan_detail = this.payment.plan_details[0];
      if (this.payment.plan_detail.is_subscription) {
        if (this.payment.plan_detail.number_of_learner > 50) {
          this.payment.plan =
            this.planTypeNameSub["medium"] +
            " (" +
            this.planNameSub[this.payment.plan_detail.package] +
            ")";
        } else {
          this.payment.plan =
            this.planTypeNameSub["lite"] +
            " (" +
            this.planNameSub[this.payment.plan_detail.package] +
            ")";
        }
      } else {
        this.payment.plan = this.planDict[this.payment.plan_detail.package];
      }
      this.payment.cycle = this.planCycleDict[this.payment.plan_detail.package];
      this.payment.price_per_month =
        this.payment.plan_detail.price_after_discount /
        PLAN_MONTH_DURATION[this.payment.plan_detail.package];
      if (this.payment.plan_detail.is_subscription) {
        this.subscriptionId = this.payment.plan_detail.payment_server_id;
        if (!this.subscriptionId) {
          await this.getSubscriptionId(this.payment.id);
        }
      }
    },
    async getSubscriptionId(orderId) {
      try {
        const { data } = await this.$axios.get(
          process.env.VUE_APP_PAYMENT_URL + "/organization/subscriptions",
          { headers: this.headers }
        );
        const subscription = data.find((item) => item.order_id == orderId);
        if (subscription) {
          this.subscriptionId = subscription.id;
        }
      } catch (e) {
        if (e.response) {
          const { detail } = e.response.data;
          warningMessage(detail, "elsa-message warning", {
            showClose: false,
            duration: 5000,
          })(this);
        }
        this.loading = false;
      }
    },
    async deleteSubscription() {
      try {
        this.loading = true;
        await this.$axios.delete(
          process.env.VUE_APP_PAYMENT_URL +
            "/organization/subscription/" +
            this.subscriptionId,
          { headers: this.headers }
        );
        setInterval(async () => {
          this.confirmDialogVisible = false;
          successMessage(
            this.$t("plan_payment.cancel_subscription.success"),
            "elsa-message success",
            { showClose: false }
          )(this);
          this.goBack();
        }, 10000);
      } catch (e) {
        if (e.response) {
          const { detail } = e.response.data;
          warningMessage(detail, "elsa-message warning", {
            showClose: false,
            duration: 5000,
          })(this);
        }
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push({
        name: "OrganizationPlan",
        params: { organizationId: this.organizationId },
      });
    },
    formatDate(date) {
      let timeZone = this.organizationData.timezone;
      if (date) {
        const zonedDate = utcToZonedTime(date, timeZone);
        return format(new Date(zonedDate), this.dateFormat);
      } else {
        return "--";
      }
    },
    formatCurrency(price, currency) {
      return new Intl.NumberFormat(currencyLocaleMapping[currency], {
        style: "currency",
        currency: currency,
      }).format(price);
    },
  },
};
</script>

<style lang="scss" scoped>
.cancel-subscription {
  width: 100%;
  height: 90vh;

  .payment {
    padding-left: 40px;
    padding-top: 32px;
    max-width: 780px;
    font-size: 18px;
    line-height: 24px;

    .title {
      font-weight: 600;
      font-size: 32px;
    }

    .desc {
      font-size: 18px;
    }

    .plan-details {
      border: 1px solid #e6e6e6;
      border-radius: 16px;
      margin-bottom: 24px;
      padding: 24px;
      max-width: 830px;
    }

    .label {
      color: #121131;
    }

    .value {
      font-weight: 600;
      color: #121131;
    }

    .el-link--danger {
      font-size: 18px;
      line-height: 24px;
      color: #eb5757;
    }
  }

  .el-dialog {
    .dialog-title {
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      color: #121131;
      word-break: break-word;
    }

    .dialog-footer {
      display: flex;
      flex-direction: column;
      row-gap: 16px;
      max-width: 300px;
      margin: auto;

      .el-button {
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        color: #eb5757;
        border: 2px solid #eb5757;
        border-radius: 100px;

        &:focus,
        &:hover {
          background-color: unset;
        }
      }

      .el-link {
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        color: #322f65;
        text-decoration-line: underline;
      }
    }
  }

  ::v-deep .el-dialog__header {
    display: none;
  }

  ::v-deep .el-dialog__body {
    text-align: inherit;
  }

  ::v-deep .el-dialog {
    min-width: 383px;
  }
}
</style>
